import React, { useEffect, useRef } from 'react';
import { sanitizeRichText } from '../../utils/richText';

// Deliberately small: sub/sup, bold, italic, clear. Enough to write
// "SHINNOKI by DECOSPAN" with the maker at the foot of the brand, without
// pulling a full editor library into a bundle that is already heavy.
//
// No font-size buttons on purpose. The visualizer sizes every collection
// heading itself so a row of them reads consistently; a label carrying its own
// sizes made one tile look nothing like its neighbours. What is authored here
// is the words and where the maker sits — <sub> says that and nothing more.
//
// `tag` actions emit a real element; `style` actions wrap in a styled span.
const ACTIONS = [
    { key: 'sub', label: 'Sub', tag: 'sub', preview: { verticalAlign: 'sub', fontSize: '0.85em' } },
    { key: 'sup', label: 'Sup', tag: 'sup', preview: { verticalAlign: 'super', fontSize: '0.85em' } },
    { key: 'bold', label: 'Bold', style: 'font-weight:600', preview: { fontWeight: 600 } },
    { key: 'italic', label: 'Italic', style: 'font-style:italic', preview: { fontStyle: 'italic' } },
];

const escapeHtml = (s) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// `showToolbar` false leaves the field itself working — text typed into it is
// still saved — and only takes the formatting buttons off the screen.
const RichLabelEditor = ({ value, onChange, placeholder, showToolbar = true }) => {
    const ref = useRef(null);

    // Only write into the node when the incoming value genuinely differs from
    // what is on screen. Rewriting innerHTML on every keystroke would drop the
    // caret to the start of the field.
    useEffect(() => {
        const el = ref.current;
        if (el && el.innerHTML !== (value || '')) {
            el.innerHTML = value || '';
        }
    }, [value]);

    const emit = () => {
        if (ref.current) onChange(sanitizeRichText(ref.current.innerHTML));
    };

    const applyAction = (action) => {
        const sel = window.getSelection();
        if (!sel || sel.isCollapsed || !ref.current?.contains(sel.anchorNode)) return;
        const text = escapeHtml(sel.toString());
        const markup = action.tag
            ? `<${action.tag}>${text}</${action.tag}>`
            : `<span style="${action.style}">${text}</span>`;
        // insertHTML keeps this to one undo step and produces clean markup,
        // unlike execCommand('fontSize') which emits legacy <font> tags.
        document.execCommand('insertHTML', false, markup);
        emit();
    };

    const clearFormatting = () => {
        const sel = window.getSelection();
        if (!sel || sel.isCollapsed || !ref.current?.contains(sel.anchorNode)) return;
        document.execCommand('insertHTML', false, escapeHtml(sel.toString()));
        emit();
    };

    return (
        <div>
            {showToolbar && (
                <div className="flex flex-wrap gap-1 mb-2">
                    {ACTIONS.map((a) => (
                        <button
                            key={a.key}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()} // keep the selection
                            onClick={() => applyAction(a)}
                            style={a.preview}
                            className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 text-gray-700"
                        >
                            {a.label}
                        </button>
                    ))}
                    <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={clearFormatting}
                        className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 text-gray-500"
                    >
                        Clear
                    </button>
                </div>
            )}

            <div
                ref={ref}
                contentEditable
                suppressContentEditableWarning
                onInput={emit}
                onBlur={emit}
                // Pasting from Word/a browser drags a mountain of markup in; take
                // the plain text and let the toolbar do the styling.
                onPaste={(e) => {
                    e.preventDefault();
                    const text = e.clipboardData.getData('text/plain');
                    document.execCommand('insertHTML', false, escapeHtml(text));
                    emit();
                }}
                data-placeholder={placeholder}
                className="w-full min-h-[46px] border border-gray-300 rounded-lg px-3 py-2 leading-snug
                    focus:outline-none focus:ring-2 focus:ring-[#9ca3af] focus:border-[#9ca3af] transition
                    empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400"
            />
        </div>
    );
};

export default RichLabelEditor;
